import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import Alert from './Alert';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import './DeleteCustomerWizard.css';

interface DeleteCustomerWizardProps {
  customerTag: string;
  customerId: string | null;
  onClose: () => void;
  onComplete: () => void;
}

interface OutstandingItem {
  sku_code: string;
  product_name: string;
  active: number;
}

/** What the operator says happened to each unit of one SKU. */
interface Disposition {
  returned: number;
  broken: number;
  lost: number;
}

const emptyDisposition = (): Disposition => ({ returned: 0, broken: 0, lost: 0 });

const assigned = (d: Disposition) => d.returned + d.broken + d.lost;

/**
 * Parse a JSON response, or fail with something an operator can act on.
 *
 * Not every response to these calls is JSON. When the local Flask backend is
 * down the dev proxy answers 504 with the plain text "Error occurred while
 * trying to proxy: ..."; behind nginx a restarting backend gives a 502 HTML
 * page; a Flask routing mistake gives a 404 HTML page. `res.json()` then throws
 * a SyntaxError whose message is the parser's complaint about the first
 * character -- "Unexpected token 'E', "Error occu"... is not valid JSON" -- and
 * the wizard rendered that verbatim, which tells the operator nothing about
 * what broke or what to do next.
 *
 * Read the body as text and report the HTTP status instead. The status is the
 * part that identifies the failure; the body is a server-side error page that
 * was never meant for this caller.
 */
async function readJson(res: Response): Promise<any> {
  const body = await res.text();
  try {
    return JSON.parse(body);
  } catch {
    // 502/504 mean nothing answered on the other side of the proxy.
    const unreachable = res.status === 502 || res.status === 503 || res.status === 504;
    throw new Error(
      unreachable
        ? `Backend not reachable (HTTP ${res.status}). Is the API server still running?`
        : `Server returned HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ''} ` +
          `instead of JSON. Nothing was changed.`
    );
  }
}

const DeleteCustomerWizard: React.FC<DeleteCustomerWizardProps> = ({
  customerTag,
  customerId,
  onClose,
  onComplete,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripeDeleted, setStripeDeleted] = useState(false);
  const [outstanding, setOutstanding] = useState<OutstandingItem[] | null>(null);
  const [dispositions, setDispositions] = useState<Record<string, Disposition>>({});
  const [rebalanced, setRebalanced] = useState(false);

  // Units the customer still holds. Anything with a negative balance has been
  // over-returned already and needs no disposition — only positives block the
  // delete, so only positives are rebalanced here.
  const owed = (outstanding || []).filter(i => i.active > 0);
  const overReturned = (outstanding || []).filter(i => i.active < 0);

  const checkStripeStatus = useCallback(async () => {
    if (!customerId) {
      setStripeDeleted(true);
      setStep(2);
    }
  }, [customerId]);

  // Read-only. The previous implementation called create-return-order here and
  // described it as a dry run, but that endpoint wrote an IN row for the whole
  // balance, so opening this wizard booked a full return before the operator
  // answered anything. That is where phantom stock came from.
  const fetchOutstanding = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/customers/${encodeURIComponent(customerTag)}/outstanding`
      );
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.error || 'Failed to check inventory');

      const items: OutstandingItem[] = data.outstanding || [];
      setOutstanding(items);
      setDispositions(
        Object.fromEntries(
          items.filter(i => i.active > 0).map(i => [i.sku_code, emptyDisposition()])
        )
      );

      if (!items.some(i => i.active > 0)) {
        setRebalanced(true);
        setStep(3);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check inventory');
    }
  }, [customerTag]);

  useEffect(() => {
    checkStripeStatus();
  }, [checkStripeStatus]);

  useEffect(() => {
    if (step === 2 && !outstanding) {
      fetchOutstanding();
    }
  }, [step, outstanding, fetchOutstanding]);

  const setField = (sku: string, field: keyof Disposition, raw: string) => {
    const item = owed.find(i => i.sku_code === sku);
    if (!item) return;
    const parsed = Math.max(0, Math.floor(Number(raw) || 0));
    setDispositions(prev => {
      const next = { ...prev[sku], [field]: Math.min(parsed, item.active) };
      // Never let the three add up past what is actually outstanding.
      if (assigned(next) > item.active) {
        const others = assigned(next) - next[field];
        next[field] = Math.max(0, item.active - others);
      }
      return { ...prev, [sku]: next };
    });
  };

  const markAllLost = () => {
    setDispositions(
      Object.fromEntries(
        owed.map(i => [i.sku_code, { returned: 0, broken: 0, lost: i.active }])
      )
    );
  };

  const markAllReturned = () => {
    setDispositions(
      Object.fromEntries(
        owed.map(i => [i.sku_code, { returned: i.active, broken: 0, lost: 0 }])
      )
    );
  };

  const remainingFor = (item: OutstandingItem) =>
    item.active - assigned(dispositions[item.sku_code] || emptyDisposition());

  const fullyAssigned = owed.length > 0 && owed.every(i => remainingFor(i) === 0);

  const handleDeleteFromStripe = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sync-health/stripe-customer`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripe_id: customerId }),
      });
      const data = await readJson(res);
      if (!res.ok) {
        throw new Error(data.detail || data.error || 'Failed to delete from Stripe');
      }
      setStripeDeleted(true);
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete from Stripe');
    } finally {
      setLoading(false);
    }
  };

  const handleRebalance = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/customers/${encodeURIComponent(customerTag)}/rebalance`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            adjustments: owed.map(i => ({
              sku_code: i.sku_code,
              ...(dispositions[i.sku_code] || emptyDisposition()),
            })),
          }),
        }
      );
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.error || 'Failed to adjust inventory');
      if (!data.balanced) {
        throw new Error(
          `Still outstanding after adjustment: ${(data.still_outstanding || [])
            .map((s: { sku_code: string; active: number }) => `${s.sku_code} x${s.active}`)
            .join(', ')}`
        );
      }
      setRebalanced(true);
      setStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to adjust inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFromMySQL = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/customers/${encodeURIComponent(customerTag)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const data = await readJson(res);
        throw new Error(
          data.detail ? `${data.error} (${data.detail})` : data.error || 'Failed to delete customer'
        );
      }
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wizard-overlay" onClick={onClose}>
      <div
        className={`wizard-modal${step === 2 && owed.length > 0 ? ' wizard-modal--wide' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="wizard-header">
          <h3>Delete Customer: {customerTag}</h3>
          <button className="wizard-close" onClick={onClose}>&times;</button>
        </div>

        {/* Step Indicator */}
        <div className="wizard-steps">
          <div className={`wizard-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Stripe</span>
          </div>
          <div className="step-connector" />
          <div className={`wizard-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Rebalance</span>
          </div>
          <div className="step-connector" />
          <div className={`wizard-step ${step >= 3 ? 'active' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Database</span>
          </div>
        </div>

        {error && (
          <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
        )}

        <div className="wizard-body">
          {/* Step 1: Stripe Deletion */}
          {step === 1 && (
            <div className="wizard-step-content">
              {!customerId ? (
                <p>No Stripe customer ID associated. Skipping Stripe deletion.</p>
              ) : stripeDeleted ? (
                <div className="step-success">Customer already deleted from Stripe.</div>
              ) : (
                <>
                  <div className="warning-box">
                    <strong>Warning:</strong> This permanently deletes the customer from Stripe. This cannot be undone.
                  </div>
                  <p>Stripe ID: <code>{customerId}</code></p>
                  <div className="wizard-actions">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button
                      variant="danger"
                      loading={loading}
                      onClick={handleDeleteFromStripe}
                    >
                      Delete from Stripe
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Rebalance every outstanding SKU to zero */}
          {step === 2 && (
            <div className="wizard-step-content">
              {!outstanding ? (
                <LoadingSpinner message="Checking inventory..." size="sm" />
              ) : (
                <>
                  <p>
                    This customer still holds <strong>{owed.reduce((a, i) => a + i.active, 0)}</strong>{' '}
                    unit(s) across <strong>{owed.length}</strong> SKU(s). Every one must be
                    recorded as returned to stock, broken, or lost before the customer can be deleted.
                  </p>

                  <div className="rebalance-shortcuts">
                    <Button variant="outline" size="sm" onClick={markAllReturned}>
                      All returned
                    </Button>
                    <Button variant="outline" size="sm" onClick={markAllLost}>
                      All lost
                    </Button>
                  </div>

                  <div className="rebalance-table-wrap">
                  <table className="rebalance-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th className="num">Out</th>
                        <th className="num">Returned</th>
                        <th className="num">Broken</th>
                        <th className="num">Lost</th>
                        <th className="num">Left</th>
                      </tr>
                    </thead>
                    <tbody>
                      {owed.map(item => {
                        const d = dispositions[item.sku_code] || emptyDisposition();
                        const left = remainingFor(item);
                        return (
                          <tr key={item.sku_code} className={left === 0 ? 'row-balanced' : ''}>
                            <td>
                              <div className="sku-name">{item.product_name}</div>
                              <div className="sku-code">{item.sku_code}</div>
                            </td>
                            <td className="num">{item.active}</td>
                            {(['returned', 'broken', 'lost'] as const).map(field => (
                              <td className="num" key={field}>
                                <input
                                  type="number"
                                  min={0}
                                  max={item.active}
                                  value={d[field]}
                                  aria-label={`${field} ${item.sku_code}`}
                                  onChange={e => setField(item.sku_code, field, e.target.value)}
                                />
                              </td>
                            ))}
                            <td className={`num ${left === 0 ? 'left-ok' : 'left-pending'}`}>
                              {left}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>

                  {overReturned.length > 0 && (
                    <div className="warning-box">
                      Note: {overReturned.map(i => `${i.sku_code} (${i.active})`).join(', ')} show a
                      negative balance — more units were booked back than went out. That is
                      pre-existing data damage and does not block deletion, but it is worth
                      correcting separately.
                    </div>
                  )}

                  <div className="wizard-actions">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button
                      variant="primary"
                      loading={loading}
                      disabled={!fullyAssigned}
                      onClick={handleRebalance}
                    >
                      {fullyAssigned
                        ? 'Record adjustments & continue'
                        : 'Account for every unit to continue'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3: MySQL Deletion */}
          {step === 3 && (
            <div className="wizard-step-content">
              {rebalanced && (
                <div className="step-success">Inventory balanced — every SKU is at zero.</div>
              )}
              <p>All checks passed. Delete <strong>{customerTag}</strong> from the inventory database?</p>
              <p className="step-detail">This will remove the customer and all associated orders, shipments, and mappings.</p>
              <div className="wizard-actions">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                  variant="danger"
                  loading={loading}
                  onClick={handleDeleteFromMySQL}
                >
                  Delete from Database
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteCustomerWizard;
